<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-300"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-200"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOpen"
        class="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4 overflow-y-auto modal-container"
        @click.self="handleBackdropClick"
      >
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 transition-opacity"
          @click="handleBackdropClick"
        ></div>

        <!-- Modal Content -->
        <Transition
          enter-active-class="transition-all duration-300"
          enter-from-class="opacity-0 scale-95 translate-y-4"
          enter-to-class="opacity-100 scale-100 translate-y-0"
          leave-active-class="transition-all duration-200"
          leave-from-class="opacity-100 scale-100 translate-y-0"
          leave-to-class="opacity-0 scale-95 translate-y-4"
        >
          <div
            v-if="isOpen"
            class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full border border-gray-200 dark:border-gray-700 transition-all duration-300 overflow-hidden flex flex-col max-h-[90vh] md:max-h-[85vh]"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="titleId"
          >
            <!-- Decorative gradient background -->
            <div 
              v-if="type === 'success'"
              class="absolute inset-0 bg-gradient-to-br from-blue-50/60 via-blue-100/40 to-blue-50/60 dark:from-blue-950/40 dark:via-blue-900/30 dark:to-blue-950/40 pointer-events-none"
            ></div>
            <!-- Animated gradient overlay -->
            <div 
              v-if="type === 'success'"
              class="absolute inset-0 bg-gradient-to-r from-transparent via-blue-200/20 to-transparent dark:via-blue-700/20 animate-shimmer pointer-events-none"
            ></div>
            <!-- Header -->
            <div 
              class="relative flex items-center justify-between p-4 md:p-6 border-b flex-shrink-0"
              :class="type === 'success' 
                ? 'border-blue-200/50 dark:border-blue-700/50 bg-gradient-to-r from-blue-50/50 to-transparent dark:from-blue-950/30' 
                : 'border-gray-200 dark:border-gray-700'"
            >
              <div class="flex items-center space-x-3">
                <!-- Icon basierend auf Typ -->
                <div
                  :class="[
                    'flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center shadow-lg',
                    type === 'success' 
                      ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 ring-2 ring-blue-200 dark:ring-blue-800' 
                      : iconClasses
                  ]"
                >
                  <component :is="iconComponent" class="w-6 h-6 md:w-7 md:h-7" />
                </div>
                <h3
                  :id="titleId"
                  class="text-lg md:text-2xl font-bold pr-2"
                  :class="type === 'success' 
                    ? 'text-blue-900 dark:text-blue-100' 
                    : 'text-gray-900 dark:text-white'"
                >
                  {{ title }}
                </h3>
              </div>
              <button
                v-if="showCloseButton"
                @click="handleClose"
                class="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 flex-shrink-0"
                aria-label="Schließen"
              >
                <svg class="w-6 h-6 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <!-- Body -->
            <div 
              ref="modalBody"
              class="relative p-4 md:p-6 overflow-y-auto flex-1 min-h-0"
              :class="type === 'success' ? 'bg-white/50 dark:bg-gray-800/50' : ''"
            >
              <div 
                class="prose prose-sm max-w-none dark:prose-invert"
                :class="type === 'success' 
                  ? 'prose-blue dark:prose-blue' 
                  : 'prose-gray dark:prose-gray'"
              >
                <p 
                  class="whitespace-pre-line leading-relaxed text-base font-medium mb-0"
                  :class="type === 'success' 
                    ? 'text-gray-800 dark:text-gray-200' 
                    : 'text-gray-700 dark:text-gray-300'"
                  v-html="message"
                ></p>
              </div>
              
              <!-- Zusätzlicher Text nach Bug-Klick -->
              <Transition
                enter-active-class="transition-all duration-500"
                enter-from-class="opacity-0 max-h-0 -translate-y-2"
                enter-to-class="opacity-100 max-h-[500px] translate-y-0"
                leave-active-class="transition-all duration-300"
                leave-from-class="opacity-100 max-h-[500px] translate-y-0"
                leave-to-class="opacity-0 max-h-0 -translate-y-2"
              >
                <div
                  v-if="bugClicked && bugMessage"
                  class="mt-4 md:mt-6 pt-4 md:pt-6 border-t-2 border-blue-300/60 dark:border-blue-600/60 overflow-hidden bg-blue-50/50 dark:bg-blue-950/30 rounded-lg p-3 md:p-4 -mx-2"
                >
                  <div class="prose prose-sm max-w-none dark:prose-invert prose-blue dark:prose-blue">
                    <p 
                      class="whitespace-pre-line leading-relaxed text-base font-medium text-gray-800 dark:text-gray-200 mb-0"
                      v-html="bugMessage"
                    ></p>
                  </div>
                </div>
              </Transition>
            </div>

            <!-- Versteckter Bug (nur bei "Maxi engagieren" Meldung) - unten links statt auf Super Button -->
            <Transition
              enter-active-class="transition-all duration-300"
              enter-from-class="opacity-0 scale-0"
              enter-to-class="opacity-100 scale-100"
              leave-active-class="transition-all duration-200"
              leave-from-class="opacity-100 scale-100"
              leave-to-class="opacity-0 scale-0"
            >
              <div
                v-if="type === 'success' && showBug && !bugClicked"
                class="absolute bottom-1 left-1 w-16 h-16 md:w-20 md:h-20 overflow-hidden cursor-pointer z-20 hover:scale-110 transition-transform duration-200 opacity-60 hover:opacity-100"
                @click="handleBugClick"
                title="Klick mich!"
              >
              <svg
                width="64"
                height="64"
                class="md:w-20 md:h-20 w-16 h-16 bug-svg-modal"
                viewBox="0 0 100 100"
                style="transform: rotate(150deg);"
              >
                <!-- Bug Body -->
                <ellipse cx="50" cy="50" rx="30" ry="20" fill="#ef4444" />
                <!-- Bug Head -->
                <circle cx="30" cy="50" r="15" fill="#ef4444" />
                <!-- Bug Eyes -->
                <circle cx="25" cy="45" r="3" fill="white" />
                <circle cx="25" cy="55" r="3" fill="white" />
                <circle cx="25" cy="45" r="1.5" fill="black" />
                <circle cx="25" cy="55" r="1.5" fill="black" />
                <!-- Bug Antennae -->
                <line x1="20" y1="40" x2="15" y2="30" stroke="black" stroke-width="2" />
                <line x1="20" y1="60" x2="15" y2="70" stroke="black" stroke-width="2" />
                <circle cx="15" cy="30" r="2" fill="black" />
                <circle cx="15" cy="70" r="2" fill="black" />
                <!-- Bug Legs -->
                <line x1="40" y1="60" x2="35" y2="75" stroke="black" stroke-width="2" />
                <line x1="50" y1="65" x2="50" y2="80" stroke="black" stroke-width="2" />
                <line x1="60" y1="60" x2="65" y2="75" stroke="black" stroke-width="2" />
                <line x1="40" y1="40" x2="35" y2="25" stroke="black" stroke-width="2" />
                <line x1="50" y1="35" x2="50" y2="20" stroke="black" stroke-width="2" />
                <line x1="60" y1="40" x2="65" y2="25" stroke="black" stroke-width="2" />
                <!-- Bug Wings -->
                <ellipse cx="55" cy="45" rx="8" ry="12" fill="#ef4444" opacity="0.6" />
                <ellipse cx="55" cy="55" rx="8" ry="12" fill="#ef4444" opacity="0.6" />
              </svg>
              </div>
            </Transition>

            <!-- Footer -->
            <div
              v-if="type === 'confirm' || showCloseButton"
              class="relative flex justify-end space-x-3 p-4 md:p-6 border-t flex-shrink-0"
              :class="type === 'success' 
                ? 'border-blue-200/50 dark:border-blue-700/50 bg-gradient-to-r from-transparent to-blue-50/30 dark:to-blue-950/20' 
                : 'border-gray-200 dark:border-gray-700'"
            >
              <button
                v-if="type === 'confirm'"
                @click="handleCancel"
                class="px-4 md:px-6 py-2 md:py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl text-sm md:text-base text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-all duration-200 font-semibold hover:scale-105"
              >
                {{ cancelText }}
              </button>
              <button
                @click="handleConfirm"
                :class="[
                  'px-4 md:px-6 py-2 md:py-2.5 rounded-xl font-semibold transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-xl text-sm md:text-base',
                  type === 'success' 
                    ? 'bg-blue-600 dark:bg-blue-500 text-white hover:bg-blue-700 dark:hover:bg-blue-600 shadow-blue-500/30 hover:shadow-blue-500/50' 
                    : confirmButtonClasses
                ]"
              >
                {{ confirmText }}
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch, onUnmounted } from 'vue'
import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/vue/24/outline'

export type ModalType = 'alert' | 'confirm' | 'info' | 'success' | 'error'

interface Props {
  isOpen: boolean
  type?: ModalType
  title?: string
  message: string
  confirmText?: string
  cancelText?: string
  showCloseButton?: boolean
  closeOnBackdrop?: boolean
  showBug?: boolean
  bugMessage?: string
}

const props = withDefaults(defineProps<Props>(), {
  type: 'info',
  title: '',
  confirmText: 'OK',
  cancelText: 'Abbrechen',
  showCloseButton: true,
  closeOnBackdrop: true,
  showBug: false,
  bugMessage: ''
})

const bugClicked = ref(false)
const modalBody = ref<HTMLElement | null>(null)

const emit = defineEmits<{
  'update:isOpen': [value: boolean]
  confirm: []
  cancel: []
  close: []
  'bug-click': []
}>()

const titleId = `modal-title-${Math.random().toString(36).substr(2, 9)}`

const isOpen = computed({
  get: () => props.isOpen,
  set: (value) => emit('update:isOpen', value)
})

const defaultTitles: Record<ModalType, string> = {
  alert: 'Hinweis',
  confirm: 'Bestätigung',
  info: 'Information',
  success: 'Erfolg',
  error: 'Fehler'
}

const title = computed(() => {
  return props.title || defaultTitles[props.type]
})

const iconComponent = computed(() => {
  switch (props.type) {
    case 'error':
      return XCircleIcon
    case 'success':
      return CheckCircleIcon
    case 'confirm':
    case 'alert':
      return ExclamationTriangleIcon
    default:
      return InformationCircleIcon
  }
})

const iconClasses = computed(() => {
  switch (props.type) {
    case 'error':
      return 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
    case 'success':
      return 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
    case 'confirm':
    case 'alert':
      return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400'
    default:
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
  }
})

const confirmButtonClasses = computed(() => {
  switch (props.type) {
    case 'error':
      return 'bg-red-600 dark:bg-red-500 text-white hover:bg-red-700 dark:hover:bg-red-600'
    case 'success':
      return 'bg-green-600 dark:bg-green-500 text-white hover:bg-green-700 dark:hover:bg-green-600'
    case 'confirm':
      return 'bg-blue-600 dark:bg-blue-500 text-white hover:bg-blue-700 dark:hover:bg-blue-600'
    default:
      return 'bg-blue-600 dark:bg-blue-500 text-white hover:bg-blue-700 dark:hover:bg-blue-600'
  }
})

const handleConfirm = () => {
  emit('confirm')
  if (props.type === 'alert' || props.type === 'info' || props.type === 'success' || props.type === 'error') {
    handleClose()
  }
}

const handleCancel = () => {
  emit('cancel')
  handleClose()
}

const handleClose = () => {
  isOpen.value = false
  emit('close')
}

const handleBackdropClick = () => {
  if (props.closeOnBackdrop) {
    handleClose()
  }
}

const handleBugClick = () => {
  // Bug als geklickt markieren
  bugClicked.value = true
  // Event an Parent senden
  emit('bug-click')

  // Zum Ende scrollen, damit der neue Text sichtbar wird
  // Wir nutzen zwei Timeouts: einen schnellen und einen nach der Animation (500ms)
  setTimeout(() => {
    if (modalBody.value) {
      modalBody.value.scrollTo({
        top: modalBody.value.scrollHeight,
        behavior: 'smooth'
      })
    }
  }, 150)

  setTimeout(() => {
    if (modalBody.value) {
      modalBody.value.scrollTo({
        top: modalBody.value.scrollHeight,
        behavior: 'smooth'
      })
    }
  }, 600)
}

// Bug-Status zurücksetzen, wenn Modal geschlossen wird
watch(isOpen, (newValue) => {
  if (!newValue) {
    bugClicked.value = false
  }
})

// ESC-Taste schließen
let escapeHandler: ((e: KeyboardEvent) => void) | null = null

watch(isOpen, (newValue) => {
  if (newValue) {
    // Bug-Status zurücksetzen beim Öffnen
    bugClicked.value = false
    // Klasse zum Body hinzufügen, wenn Modal offen ist
    document.body.classList.add('modal-open')
    escapeHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && props.closeOnBackdrop) {
        handleClose()
      }
    }
    document.addEventListener('keydown', escapeHandler)
  } else if (escapeHandler) {
    // Klasse vom Body entfernen, wenn Modal geschlossen wird
    document.body.classList.remove('modal-open')
    document.removeEventListener('keydown', escapeHandler)
    escapeHandler = null
  }
})

onUnmounted(() => {
  // Stelle sicher, dass die Klasse entfernt wird, wenn Komponente unmounted wird
  document.body.classList.remove('modal-open')
  if (escapeHandler) {
    document.removeEventListener('keydown', escapeHandler)
  }
})
</script>

<style scoped>
@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.animate-shimmer {
  animation: shimmer 3s ease-in-out infinite;
}

.bug-svg-modal {
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  transition: all 0.2s ease-out;
}

.bug-svg-modal:hover {
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.4));
  transform: rotate(150deg) scale(1.1) !important;
}

/* Cursor-Sichtbarkeit in Modalen optimieren */
.modal-container,
.modal-container * {
  cursor: auto !important;
}

/* Custom-Cursor soll über Modal liegen */
:global(.cursor-wrapper) {
  z-index: 10000 !important;
}

/* Wenn Modal offen ist, Custom-Cursor sichtbar machen */
:global(body.modal-open .cursor-wrapper) {
  z-index: 10000 !important;
}
</style>

